import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [details, setDetails] = useState(null);
  const { clear } = useCart();
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attemptsRef.current < 6) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch {
        if (attemptsRef.current < 6) setTimeout(poll, 2000);
        else setStatus("error");
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="px-6 md:px-12 lg:px-16 py-24 min-h-[60vh] flex items-center justify-center" data-testid="payment-success-page">
      <div className="max-w-xl text-center">
        {status === "checking" && (
          <>
            <Clock className="w-16 h-16 text-halo-navy mx-auto animate-pulse" strokeWidth={1.5} />
            <h1 className="font-sports uppercase text-4xl mt-6 text-halo-navy">Confirming Payment…</h1>
            <p className="font-body text-halo-navy/60 mt-3">Hang tight, checking with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-20 h-20 text-halo-red mx-auto" strokeWidth={2} />
            <h1 className="font-sports uppercase text-5xl mt-6 text-halo-navy">You're Locked In!</h1>
            <p className="font-body text-halo-navy/70 mt-4 text-lg">
              Order confirmed{details?.amount_total ? ` for $${(details.amount_total / 100).toFixed(2)}` : ""}.
              You'll get an email receipt shortly.
            </p>
            <div className="flex gap-3 justify-center mt-8">
              <Link data-testid="ps-home-btn" to="/" className="bg-halo-navy text-halo-cream px-6 py-3 font-sports uppercase tracking-wider text-sm">Back Home</Link>
              <Link data-testid="ps-shop-btn" to="/shop" className="border-2 border-halo-navy text-halo-navy hover:bg-halo-navy hover:text-halo-cream px-6 py-3 font-sports uppercase tracking-wider text-sm">Keep Shopping</Link>
            </div>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <XCircle className="w-16 h-16 text-halo-red mx-auto" strokeWidth={1.5} />
            <h1 className="font-sports uppercase text-4xl mt-6 text-halo-navy">Payment Not Completed</h1>
            <p className="font-body text-halo-navy/60 mt-3">
              {status === "expired" ? "Session expired." : "We couldn't confirm your payment."}
            </p>
            <Link to="/cart" data-testid="ps-back-cart" className="inline-block mt-6 bg-halo-red text-white px-6 py-3 font-sports uppercase tracking-wider text-sm">
              Back to Cart
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
